'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import styles from './layout.module.css';
import { buttonClass, Icon, type ButtonVariant } from '@/components/ui';
import { ROUTES } from '@/lib/site';

export type NavItem = { label: string; href: string };
export type HeaderAction = {
  label: string;
  href: string;
  variant: ButtonVariant;
  icon?: 'user';
};

type SiteHeaderProps = {
  nav: NavItem[];
  actions: HeaderAction[];
  /** Selo ao lado do logo (ex.: "Vitrine"). */
  badge?: string;
  homeHref?: string;
};

export function SiteHeader({
  nav,
  actions,
  badge,
  homeHref = ROUTES.home,
}: SiteHeaderProps) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const close = () => setOpen(false);

  return (
    // O slot guarda o espaço do header no fluxo; o header em si fica fixo no topo.
    <div className={styles.headerSlot}>
      <header
        className={`${styles.header} ${scrolled ? styles.headerScrolled : ''}`}
      >
        <Link
          href={homeHref}
          className={styles.brand}
          aria-label={badge ? `Yuha! ${badge} — início` : 'Yuha! — início'}
        >
          <Image
            src='/global/yuhaLogo.svg'
            alt='Yuha!'
            width={91}
            height={32}
            className={styles.logo}
            priority
          />
          {badge && <span className={styles.brandBadge}>{badge}</span>}
        </Link>

        <nav aria-label='Principal' className={styles.nav}>
          {nav.map(item => (
            <a key={item.label} href={item.href}>
              {item.label}
            </a>
          ))}
        </nav>

        <div className={styles.actions}>
          {actions.map(action => (
            <a
              key={action.label}
              href={action.href}
              className={buttonClass(action.variant)}
            >
              {action.icon && <Icon name={action.icon} size={16} />}
              {action.label}
            </a>
          ))}
        </div>

        <button
          type='button'
          className={styles.menuButton}
          aria-label={open ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={open}
          aria-controls='menu-celular'
          onClick={() => setOpen(v => !v)}
        >
          <Icon name={open ? 'x' : 'menu'} size={20} />
        </button>

        {open && (
          <div id='menu-celular' className={styles.mobileMenu}>
            <nav aria-label='Principal (celular)'>
              {nav.map(item => (
                <a key={item.label} href={item.href} onClick={close}>
                  {item.label}
                </a>
              ))}
            </nav>
            <div className={styles.mobileActions}>
              {actions.map(action => (
                <a
                  key={action.label}
                  href={action.href}
                  className={buttonClass(action.variant, 'md', true)}
                  onClick={close}
                >
                  {action.icon && <Icon name={action.icon} size={16} />}
                  {action.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </header>
    </div>
  );
}
